import { useMemo, useState } from 'react'

type RecordRow = Record<string, unknown>

type ServiceItem = {
  id: number | string
  categoria: string
  item: string
  tarifa: string
}

type Props = {
  records: RecordRow[]
  source: 'online' | 'cache'
  savedAt: string | null
  loading: boolean
  error: string
}

function text(value: unknown) {
  return value == null ? '' : String(value).trim()
}

function normalize(value: string) {
  return value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLocaleLowerCase('pt-BR')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()
}

function toItem(row: RecordRow, index: number): ServiceItem {
  return {
    id: (row.id as number | string | undefined) ?? index,
    categoria: text(row.categoria) || 'Sem categoria',
    item: text(row.item) || 'Item não informado',
    tarifa: text(row.tarifa) || '—',
  }
}

async function copyText(value: string) {
  try {
    await navigator.clipboard.writeText(value)
    return true
  } catch {
    return false
  }
}

export function TabelaServicosOperational({ records, source, savedAt, loading, error }: Props) {
  const [query, setQuery] = useState('')
  const [category, setCategory] = useState('todas')
  const [message, setMessage] = useState('')

  const items = useMemo(() => records.map(toItem), [records])
  const categories = useMemo(() => Array.from(new Set(items.map((item) => item.categoria))).sort((a, b) => a.localeCompare(b, 'pt-BR')), [items])

  const grouped = useMemo(() => {
    const needle = normalize(query)
    const filtered = items.filter((item) => {
      if (category !== 'todas' && item.categoria !== category) return false
      if (!needle) return true
      return normalize(`${item.categoria} ${item.item} ${item.tarifa}`).includes(needle)
    })

    const groups = new Map<string, ServiceItem[]>()
    filtered.forEach((item) => {
      const list = groups.get(item.categoria) ?? []
      list.push(item)
      groups.set(item.categoria, list)
    })

    return Array.from(groups.entries())
      .map(([categoria, entries]) => ({ categoria, entries }))
      .sort((a, b) => a.categoria.localeCompare(b.categoria, 'pt-BR'))
  }, [items, query, category])

  const visibleCount = grouped.reduce((total, group) => total + group.entries.length, 0)

  async function handleCopy(item: ServiceItem) {
    const value = `${item.categoria} — ${item.item}: ${item.tarifa}`
    const ok = await copyText(value)
    setMessage(ok ? 'Serviço e tarifa copiados.' : 'Não foi possível copiar automaticamente.')
    window.setTimeout(() => setMessage(''), 1800)
  }

  return (
    <section className="services-operational-tool">
      <div className="tool-heading">
        <div>
          <p className="eyebrow">CONSULTA OPERACIONAL</p>
          <h2>Tabela de Serviços</h2>
          <p className="muted">Consulte serviços, condições de cobrança e tarifas publicadas no Suite.</p>
        </div>
        <span className={`status-chip ${source === 'cache' ? 'warning' : ''}`}>{source === 'cache' ? 'Contingência' : 'Base publicada'}</span>
      </div>

      <section className="services-toolbar-card">
        <div className="services-toolbar-grid">
          <label>
            Buscar serviço, condição ou tarifa
            <input
              type="search"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Ex.: religação, asfalto, 3x, registro..."
            />
          </label>
          <label>
            Categoria
            <select value={category} onChange={(event) => setCategory(event.target.value)}>
              <option value="todas">Todas as categorias</option>
              {categories.map((item) => <option key={item} value={item}>{item}</option>)}
            </select>
          </label>
          <button className="secondary-button" type="button" onClick={() => { setQuery(''); setCategory('todas') }}>Limpar filtros</button>
        </div>

        <div className="services-summary-row">
          <span><strong>{visibleCount}</strong> de {items.length} item(ns) exibido(s)</span>
          <span><strong>{grouped.length}</strong> categoria(s)</span>
          {source === 'cache' && savedAt && <span>Sincronizado em {new Date(savedAt).toLocaleString('pt-BR')}</span>}
        </div>
        {message && <div className="tool-status ok services-copy-message">{message}</div>}
      </section>

      {loading && <div className="empty-state">Carregando tabela de serviços...</div>}
      {error && <div className="notice error">{error}</div>}

      {!loading && !error && grouped.length === 0 && (
        <div className="empty-state">Nenhum serviço encontrado para os filtros informados.</div>
      )}

      {!loading && !error && grouped.length > 0 && (
        <div className="services-category-grid">
          {grouped.map((group) => (
            <article className="services-category-card" key={group.categoria}>
              <header>
                <div>
                  <p className="eyebrow">CATEGORIA</p>
                  <h3>{group.categoria}</h3>
                </div>
                <span>{group.entries.length} item(ns)</span>
              </header>

              <div className="services-item-list">
                {group.entries.map((item) => (
                  <div className="services-item-row" key={item.id}>
                    <div>
                      <strong>{item.item}</strong>
                      <small>{item.categoria}</small>
                    </div>
                    <div className="services-price-block">
                      <span>Tarifa</span>
                      <strong>{item.tarifa}</strong>
                    </div>
                    <button type="button" onClick={() => void handleCopy(item)}>Copiar</button>
                  </div>
                ))}
              </div>
            </article>
          ))}
        </div>
      )}
    </section>
  )
}
